import {
    Background,
    CFlex,
    CMargin,
    DText,
    Flex,
} from "../../../../styles/CommonStyles";
import Input from "../../../common/input/Input";
import ActionButton from "../../../common/buttons/ActionButton";
import { useEffect, useMemo, useState } from "react";
import { useQueryContext } from "../../../../core/contexts/query";
import {
    useAddPackage,
    useEditPackage,
    useDelPackage,
    useGetAPackageWithId,
} from "../../../../core/services/react-query/setting/package";
import BasicModalActionButton from "../../../common/buttons/BasicModalActionButton";
import Select from "../../../common/dropdown/Select";

const LANGS = ["fa", "en"];
const MONTHS = [1, 3, 6, 12];

const initialPackage = {
    id: null,
    duration: 1,
    price: "",
    assistant_limit: "",
    trigger_limit: "",
    fastscan_limit: "",
    name: { fa: "", en: "" },
    description: { fa: "", en: "" },
    features: {
        fa: [{ key: "", value: "" }],
        en: [{ key: "", value: "" }],
    },
};

const PackageModal = ({ onSuccess, type = "add", _defaultPackage, closeModal }) => {
    const { setToast } = useQueryContext();


    const { data: _package, isLoading: isPackageLoading } = useGetAPackageWithId({
        id: _defaultPackage?.id,
        enabled: !!_defaultPackage?.id && type !== 'add'
    })

    const { mutate: addPackage, isLoading: isAdding } =
        useAddPackage(onSuccess);

    const { mutate: editPackage, isLoading: isEditing } =
        useEditPackage(onSuccess);

    const { mutate: deletePackage, isLoading: isDeleting } =
        useDelPackage(onSuccess);

    const [pkg, setPkg] = useState(initialPackage);

    /* ------------------ Mappers ------------------ */

    const mapLangArrayToObject = (arr = []) => {
        const result = { fa: "", en: "" };
        arr.forEach((item) => {
            result[item.language] = item.value;
        });
        return result;
    };

    const mapObjectToLangArray = (obj) =>
        LANGS.map((lang) => ({
            language: lang,
            value: obj[lang] || "",
        }));

    const mapFeaturesArrayToObject = (arr = []) => {
        const result = { fa: [], en: [] };
        arr.forEach((langBlock) => {
            result[langBlock.language] =
                langBlock.items?.length
                    ? langBlock.items
                    : [{ key: "", value: "" }];
        });
        return result;
    };

    const mapFeaturesToArray = (featuresObj) =>
        LANGS.map((lang) => ({
            language: lang,
            items: featuresObj[lang]
                .filter((f) => f.key && f.value)
                .map((f) => ({
                    key: f.key,
                    value: f.value,
                })),
        }));

    /* ------------------ Edit Mode ------------------ */

    useEffect(() => {
        if (_package && type === "edit") {
            setPkg({
                id: _package.id,
                duration: _package.duration,
                price: _package.price,
                assistant_limit: _package.assistant_limit,
                trigger_limit: _package.trigger_limit,
                fastscan_limit: _package.fastscan_limit,
                name: mapLangArrayToObject(_package.names),
                description: mapLangArrayToObject(_package.descriptions),
                features: mapFeaturesArrayToObject(_package.features),
            });
        }
    }, [_package, isPackageLoading]);

    /* ------------------ Handlers ------------------ */

    const onInputChange = (key, value) => {
        setPkg((s) => ({ ...s, [key]: value }));
    };

    const onLangChange = (field, lang, value) => {
        setPkg((s) => ({
            ...s,
            [field]: {
                ...s[field],
                [lang]: value,
            },
        }));
    };

    const addFeature = (lang) => {
        setPkg((s) => ({
            ...s,
            features: {
                ...s.features,
                [lang]: [...s.features[lang], { key: "", value: "" }],
            },
        }));
    };

    const removeFeature = (lang, index) => {
        setPkg((s) => ({
            ...s,
            features: {
                ...s.features,
                [lang]: s.features[lang].filter((_, i) => i !== index),
            },
        }));
    };

    const onFeatureChange = (lang, index, field, value) => {
        setPkg((s) => {
            const updated = [...s.features[lang]];
            updated[index][field] = value;
            return {
                ...s,
                features: {
                    ...s.features,
                    [lang]: updated,
                },
            };
        });
    };

    /* ------------------ Validation ------------------ */

    const validAction = useMemo(() => {
        if (type === "delete") return true;
        return (
            pkg.price !== "" &&
            pkg.duration &&
            (pkg.name.fa || pkg.name.en)
        );
    }, [pkg, type]);

    /* ------------------ Submit ------------------ */

    const onSubmit = () => {
        console.log(_package);

        if (type === "delete") {
            deletePackage(_package.id);
            return;
        }

        if (!validAction) {
            setToast({
                message: "fill-required-fields",
                show: true,
                isError: true,
            });
            return;
        }

        const payload = {
            duration: Number(pkg.duration),
            price: Number(pkg.price),
            assistant_limit: Number(pkg.assistant_limit),
            trigger_limit: Number(pkg.trigger_limit),
            fastscan_limit: Number(pkg.fastscan_limit),
            name: mapObjectToLangArray(pkg.name),
            description: mapObjectToLangArray(pkg.description),
            features: mapFeaturesToArray(pkg.features),
        };

        if (type === "add") {
            addPackage(payload);
        } else {
            editPackage({ id: pkg.id, ...payload }, _defaultPackage.id);
        }
    };

    /* ------------------ DELETE UI ------------------ */

    if (type === "delete") {
        return (
            <CFlex fw>
                <DText main fontSize="b">
                    Delete Plan
                </DText>

                <CMargin margin="20px" />

                <DText main>
                    Are you sure you want to delete this plan?
                </DText>

                <CMargin margin="24px" />

                <BasicModalActionButton
                    onSubmit={onSubmit}
                    onCancel={closeModal}
                    loading={isDeleting}
                />
            </CFlex>
        );
    }

    /* ------------------ ADD / EDIT UI ------------------ */

    return (
        <CFlex fw>
            <DText main fontSize="b">
                {type === "add" ? "Add Plan" : "Edit Plan"}
            </DText>

            <CMargin margin="16px" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full">

                <Background>

                    <Select options={MONTHS} value={pkg.duration} onValueChange={(idx) => onInputChange("duration", MONTHS[idx])} />
                </Background>

                <Background >
                    <Input
                        label="Price"
                        value={pkg.price}
                        onInputChange={(v) => onInputChange("price", v)}
                    />
                </Background>

                <Background>
                    <Input
                        label="Assistant Limit"
                        value={pkg.assistant_limit}
                        onInputChange={(v) => onInputChange("assistant_limit", v)}
                    />
                </Background>

                <Background>
                    <Input
                        label="Trigger Limit"
                        value={pkg.trigger_limit}
                        onInputChange={(v) => onInputChange("trigger_limit", v)}
                    />
                </Background>

                <Background>
                    <Input

                        label="FastScan Limit"
                        value={pkg.fastscan_limit}
                        onInputChange={(v) => onInputChange("fastscan_limit", v)}
                    />
                </Background>

                {LANGS.map((lang) => (
                    <Background key={`name-${lang}`}>
                        <Input
                            label={`Name (${lang})`}
                            value={pkg.name[lang]}
                            onInputChange={(v) =>
                                onLangChange("name", lang, v)
                            }
                        />
                    </Background>
                ))}

                {LANGS.map((lang) => (
                    <Background
                        key={`desc-${lang}`}

                        className="md:col-span-2"
                    >
                        <Input
                            label={`Description (${lang})`}
                            value={pkg.description[lang]}
                            onInputChange={(v) =>
                                onLangChange("description", lang, v)
                            }
                        />
                    </Background>
                ))}

                {LANGS.map((lang) => (
                    <div key={`features-${lang}`} className="md:col-span-2">
                        <DText main>{`Features (${lang})`}</DText>

                        {pkg.features[lang].map((feature, index) => (
                            <Flex key={index} gap="10px">
                                <Input
                                    label="Key"
                                    value={feature.key}
                                    onInputChange={(v) =>
                                        onFeatureChange(lang, index, "key", v)
                                    }
                                />
                                <Input
                                    label="Value"
                                    value={feature.value}
                                    onInputChange={(v) =>
                                        onFeatureChange(lang, index, "value", v)
                                    }
                                />
                                <ActionButton
                                    width="32px"
                                    type="danger"
                                    onClick={() =>
                                        removeFeature(lang, index)
                                    }
                                >
                                    X
                                </ActionButton>
                            </Flex>
                        ))}

                        <CMargin margin="8px" />

                        <ActionButton onClick={() => addFeature(lang)}>
                            Add Feature ({lang})
                        </ActionButton>
                    </div>
                ))}
            </div>

            <CMargin margin="20px" />

            <ActionButton
                active={validAction}
                height="42px"
                onClick={onSubmit}
                loading={isPackageLoading || isAdding || isEditing}
            >
                Submit
            </ActionButton>
        </CFlex>
    );
};

export default PackageModal;